import ENV from "../constants/env"
import { allergenFilter, dietFilter } from "../homepage/homepage.utils"
import { User, UserSettings } from "../main/main.types"
import algoliaClient from "./algoliaClient"
import { COOKING_TIME_DICTIONARY, DIET_DICTIONARY } from "./search.constants"
import { SearchFilteredRecipesInterface, SearchFiltersInterface, SearchIngredients } from "./search.types"

export const dietSelectionStringHandler = (userDiet: string | undefined, user_id: string | undefined) => {

  if (userDiet && DIET_DICTIONARY.hasOwnProperty(userDiet)) {
    return `${DIET_DICTIONARY[userDiet]} OR created_by:${user_id}`
  }
  return ''
}

export const cookingTimeSelectionHandler = (cookingTime: string | undefined) => {
  if (cookingTime && COOKING_TIME_DICTIONARY.hasOwnProperty(cookingTime)) {
    return `${COOKING_TIME_DICTIONARY[cookingTime]}`
  }
  return ''
}

export const filterIngredientsSource = (user_id: string) => `created_source:recipeAdmin OR created_source:chefgpt OR created_by:${user_id}`

export const servingSizeSelectionHandler = (servingSizeList: string[], user_id: string | undefined) => {
  let minServingSize = 6;
  let maxServingSize = 0;
  servingSizeList.forEach((servingSizeString: string) => {
    const servingSize = parseInt(servingSizeString[0], 10)
    minServingSize = Math.min(minServingSize, servingSize)
    maxServingSize = Math.max(maxServingSize, servingSize)
  })

  return servingSizeList.length !== 0 ? `min_serving_size <= ${maxServingSize} AND max_serving_size >= ${minServingSize} OR created_by:${user_id}` : ''
}

export const filterByFlagActiveStatus = (user: User | undefined, enableAdminFeatures: boolean) => {
  if (enableAdminFeatures) {
    return ''
  } return `is_public:true OR created_by:${user?.id}`
}

export const filterStringHandler = (user: User | undefined, searchFilters: SearchFiltersInterface, enableAdminFeatures: boolean) => {
  const resultFiltersArray: Array<string> = [];
  const resultDietPreferenceString: string = dietSelectionStringHandler(user?.userSettings?.diet, user?.id)
  const resultCookingTimeString: string = cookingTimeSelectionHandler(searchFilters.timeFilter)
  const resultActiveRecipesString = filterByFlagActiveStatus(user, enableAdminFeatures);
  const resultServingSizeString: string = servingSizeSelectionHandler(searchFilters.servingSizeFilter, user?.id)

  if (searchFilters.cuisineFilter.length) {
    const resultCuisineArray: string[] = []
    searchFilters.cuisineFilter.forEach((cuisine: string) => {
      if (cuisine) resultCuisineArray.push(`cuisine:"${cuisine}"`)
    })
    resultFiltersArray.push(resultCuisineArray.join(" OR "))
  }

  if (searchFilters.ingredientFilter.length) {
    const resultIngredientsArray: string[] = []
    searchFilters.ingredientFilter.forEach((ingredient: string) => {
      if (ingredient) resultIngredientsArray.push(`ingredients:"${ingredient}"`)
    })
    resultFiltersArray.push(resultIngredientsArray.join(" OR "))
  }

  if (searchFilters.mealTypeFilter.length) {
    const resultMealTypeArray: string[] = []
    searchFilters.mealTypeFilter.forEach((mealType: string) => {
      if (mealType) resultMealTypeArray.push(`meal_type:"${mealType}"`)
    })
    resultFiltersArray.push(resultMealTypeArray.join(" OR "))
  }
  const userAllergens = user?.userSettings?.allergens ?? []
  if (userAllergens.length) {
    userAllergens.forEach((allergen: string) => {
      if (allergen) {
        resultFiltersArray.push(`NOT allergens:"${allergen}"`)
      }
    })
  }
  if (resultDietPreferenceString) resultFiltersArray.push(resultDietPreferenceString)
  if (resultCookingTimeString) resultFiltersArray.push(resultCookingTimeString)
  if (resultActiveRecipesString) resultFiltersArray.push(resultActiveRecipesString)
  if (resultServingSizeString) resultFiltersArray.push(resultServingSizeString)
  return resultFiltersArray.join(" AND ")
}

export const isLowerCaseIncludes = (stringToCheck: string, queryString: string) => stringToCheck.toLowerCase().includes(queryString.toLowerCase())

export const getServiceSearchResults = async (queryString: string, filterString: string) => {
  const index = algoliaClient.initIndex(ENV.ALGOLIA_INDEX_NAME ?? "")
  const res = await index.search(queryString, {
    filters: filterString
  })
  const resultRecipeList = res.hits.map((hit: any) => {
    const { id, name, image_url, cooking_time: cookingTime, diet, cuisine, prep_time: prepTime, max_serving_size: maxServing, min_serving_size: minServing }: { id: string, name: string, image_url: string, cooking_time: number, diet: string[], cuisine: string[], prep_time: number, max_serving_size: number, min_serving_size: number } = hit
    return {
      id,
      metadata: { name, url: image_url, cookingTime, diet, cuisine, prepTime, maxServing, minServing }
    }
  })
  return resultRecipeList
}

export const getServiceIngredientsResults = async (queryString: string, filterString: string) => {
  const index = algoliaClient.initIndex(ENV.ALGOLIA_INGREDINETS_INDEX_NAME ?? "")
  const res = await index.search(queryString, {
    filters: filterString
  })
  const resultIngredientList = res.hits.map((hit: any) => {
    const { id, name, image_url, defaultWeightUnit, secondaryUnit, created_by, created_source, alternate_name: alternateNames }: { id: string, name: string, image_url: string, defaultWeightUnit: string, secondaryUnit: string, created_by: string, created_source: string, alternate_name: string[] } = hit
    return {
      id,
      metadata: { name, url: image_url, defaultWeightUnit, secondaryUnit, created_by, created_source, alternateNames }
    }
  })
  return resultIngredientList
}

export const filterRecipes = (userSettings: UserSettings, recipeList: SearchFilteredRecipesInterface[], searchFilters: SearchFiltersInterface) => {
  const diet = userSettings?.diet ?? "";
  const allergens = userSettings?.allergens ?? [];
  function filterCuisine(recipe: any) {
    const recipeContainsCuisine = searchFilters.cuisineFilter.some((cuisine: any) => recipe.metadata.tags.includes(cuisine))
    if (searchFilters.cuisineFilter.length === 0 || recipeContainsCuisine) return true
    return false;
  }

  function filterIngredients(recipe: any) {
    const recipeContainsIngredient = searchFilters.ingredientFilter.some((ingredient: any) => recipe.metadata.ingredients.includes(ingredient))
    if (searchFilters.ingredientFilter.length === 0 || recipeContainsIngredient) return true
    return false;
  }

  function filterCourses(recipe: any) {

    if (
      searchFilters.courseFilter === null ||
      searchFilters.courseFilter === "" ||
      recipe.metadata.categories.includes(searchFilters.courseFilter)
    ) return true;

    return false;
  }

  function filterMealType(recipe: any) {
    if (searchFilters.mealTypeFilter.length === 0 || searchFilters.mealTypeFilter.some((mealType: any) =>
      recipe.metadata.mealType.includes(mealType)
    )) {
      return true;
    }
    return false;
  }

  function filterServingSize(recipe: any) {
    if (searchFilters.servingSizeFilter.length === 0 || searchFilters.servingSizeFilter.some((servingSize: any) => {
      const servingString = servingSize.includes("persons") ? servingSize.replace(" persons", "") : servingSize.replace(" person", "");
      return recipe.metadata.minServing <= parseInt(servingString, 10) && recipe.metadata.maxServing >= parseInt(servingString, 10);
    })) {
      return true;
    }
    return false;
  }

  function filterTime(recipe: any) {
    if (
      searchFilters.timeFilter === null ||
      searchFilters.timeFilter === "" ||
      searchFilters.timeFilter === "Any"
    ) {
      return true;
    }
    if (
      searchFilters.timeFilter === "Below 30 mins" &&
      recipe.metadata.prepTime + recipe.metadata.cookTime <= 30
    ) {
      return true;
    }
    if (
      searchFilters.timeFilter === "More than 30 mins" &&
      recipe.metadata.prepTime + recipe.metadata.cookTime > 30
    ) {
      return true;
    }
    return false;
  }

  const localRecipeMetadata = recipeList;
  const preferencesFilter = localRecipeMetadata.filter((recipe: any) => {
    const recipeDiet = recipe.metadata.categories;
    const recipeAllergens = recipe.allergens;
    if (!dietFilter(diet, recipeDiet)) return false;
    if (allergenFilter(allergens, recipeAllergens)) return false;
    return true;
  });
  const userFilteredRecipes = preferencesFilter
    .filter(filterCuisine)
    .filter(filterIngredients)
    .filter(filterCourses)
    .filter(filterTime)
    .filter(filterMealType)
    .filter(filterServingSize)
  return userFilteredRecipes;

}